import React, { useEffect, useMemo, useState } from 'react';
import api from '../utils/api';

const MONTHS = ['Januari','Februari','Maret','April','Mei','Juni','Juli','Agustus','September','Oktober','November','Desember'];
const DOW = ['M','S','S','R','K','J','S'];

// Local-timezone-safe date key (avoid UTC shift that pushes date to previous day in WIB/GMT+7)
const dateKey = (d) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};
const sameDay = (a, b) => a && b && a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();

/**
 * Compact month calendar showing existing scheduled work per day with load coloring.
 * Yellow: 2-3 maintenance/IT tickets. Red: 4+.
 */
const MiniScheduleCalendar = ({ value, onSelect, excludeTicketId, category }) => {
  const initial = value ? new Date(`${value}T00:00:00`) : new Date();
  const [cursor, setCursor] = useState(new Date(initial.getFullYear(), initial.getMonth(), 1));
  const [tickets, setTickets] = useState([]);

  useEffect(() => {
    const start = new Date(cursor.getFullYear(), cursor.getMonth(), 1);
    const endD = new Date(cursor.getFullYear(), cursor.getMonth() + 1, 1);
    api.get(`/tickets/schedule/calendar?start=${dateKey(start)}&end=${dateKey(endD)}`)
      .then((r) => setTickets(r.data || []))
      .catch(() => setTickets([]));
  }, [cursor]);

  const relevant = useMemo(() => {
    const cats = new Set(['umum', 'me', 'it']);
    return tickets.filter((t) => t.ticket_number !== excludeTicketId && cats.has(t.category));
  }, [tickets, excludeTicketId]);

  const dayInfo = useMemo(() => {
    const map = {};
    for (const t of relevant) {
      const raw = t.scheduled_start;
      if (!raw) continue;
      const k = String(raw).slice(0, 10);
      if (!map[k]) map[k] = { count: 0, titles: [] };
      map[k].count += 1;
      if (map[k].titles.length < 3) map[k].titles.push(`${t.ticket_number} — ${t.title}`);
    }
    return map;
  }, [relevant]);

  const cells = useMemo(() => {
    const firstDow = new Date(cursor.getFullYear(), cursor.getMonth(), 1).getDay();
    const daysInMonth = new Date(cursor.getFullYear(), cursor.getMonth() + 1, 0).getDate();
    const arr = [];
    for (let i = 0; i < firstDow; i++) arr.push(null);
    for (let d = 1; d <= daysInMonth; d++) arr.push(new Date(cursor.getFullYear(), cursor.getMonth(), d));
    // Pad trailing so last row completes to 7 columns (Sabtu always clickable)
    while (arr.length % 7 !== 0) arr.push(null);
    return arr;
  }, [cursor]);

  const showLoadColors = category && ['umum', 'me', 'it'].includes(category);
  const selectedDate = value ? new Date(`${value}T00:00:00`) : null;
  const today = new Date();

  return (
    <div className="border border-border rounded-md p-2 bg-background text-xs" data-testid="mini-schedule-calendar">
      <div className="flex items-center justify-between mb-1.5">
        <button type="button"
          onClick={() => setCursor(new Date(cursor.getFullYear(), cursor.getMonth() - 1, 1))}
          className="px-1.5 py-0.5 rounded hover:bg-secondary text-xs"
          data-testid="mini-cal-prev">‹</button>
        <span className="font-medium text-xs">{MONTHS[cursor.getMonth()]} {cursor.getFullYear()}</span>
        <button type="button"
          onClick={() => setCursor(new Date(cursor.getFullYear(), cursor.getMonth() + 1, 1))}
          className="px-1.5 py-0.5 rounded hover:bg-secondary text-xs"
          data-testid="mini-cal-next">›</button>
      </div>
      <div className="grid grid-cols-7 gap-0.5">
        {DOW.map((d, i) => <div key={`dow-${i}`} className="text-center text-[9px] text-muted-foreground py-0.5">{d}</div>)}
        {cells.map((d, i) => {
          if (!d) return <div key={`empty-${i}`} className="min-h-[24px]" />;
          const key = dateKey(d);
          const info = dayInfo[key];
          const count = info?.count || 0;
          const isSelected = selectedDate && sameDay(d, selectedDate);
          const isToday = sameDay(d, today);
          let cls = 'text-center py-1 rounded cursor-pointer text-[11px] transition-colors min-w-0';
          if (isSelected) cls += ' bg-primary text-primary-foreground font-bold';
          else if (showLoadColors && count >= 4) cls += ' bg-red-100 text-red-800 hover:bg-red-200 font-semibold';
          else if (showLoadColors && count >= 2) cls += ' bg-amber-100 text-amber-800 hover:bg-amber-200 font-semibold';
          else if (count > 0) cls += ' bg-secondary/70 hover:bg-secondary';
          else cls += ' hover:bg-secondary/40';
          if (isToday && !isSelected) cls += ' ring-1 ring-primary/40';

          return (
            <button key={key} type="button"
              onClick={() => onSelect && onSelect(key)}
              className={cls}
              title={info?.titles?.join('\n') || 'Kosong'}
              data-testid={`mini-cal-day-${key}`}>
              {d.getDate()}
              {count > 0 && (
                <span className="block text-[8px] leading-tight opacity-80">{count}📌</span>
              )}
            </button>
          );
        })}
      </div>
      {showLoadColors && (
        <div className="flex items-center gap-2 mt-1.5 text-[9px] text-muted-foreground">
          <span className="flex items-center gap-1"><span className="w-2 h-2 rounded bg-amber-100 border border-amber-300"></span>2-3</span>
          <span className="flex items-center gap-1"><span className="w-2 h-2 rounded bg-red-100 border border-red-300"></span>4+</span>
          <span className="flex items-center gap-1"><span className="w-2 h-2 rounded bg-secondary/70"></span>1</span>
        </div>
      )}
    </div>
  );
};

export default MiniScheduleCalendar;
